// Turns DeerActivity/Get days into what the Deer Activity card draws. Kept free of React so it can be tested.

export const BANDS = ['Low', 'Fair', 'Good', 'Peak']

// The Blaze ramp. HuntWind owns green / yellow / red, so activity never uses them.
export const RAMP = { Low: '#a8a097', Fair: '#d8924d', Good: '#f08a24', Peak: '#ffb35c' }
const NIGHT = '#4e4e4e'
const OFF = '#4a4a4a'
const LEGAL_LIGHT_MINUTES = 30

export const bandOf = score => score >= 75 ? 'Peak' : score >= 50 ? 'Good' : score >= 25 ? 'Fair' : 'Low'

// "06:52" or "6:52 am" -> minutes after midnight.
export function minutesOf(time) {
  const m = /^(\d{1,2}):(\d{2})\s*(am|pm)?$/i.exec((time || '').trim())
  if (!m) return null
  let h = +m[1]
  const ampm = m[3] && m[3].toLowerCase()
  if (ampm) h = (h % 12) + (ampm === 'pm' ? 12 : 0)
  return h * 60 + +m[2]
}

// "06:52" -> "6:52 am".
export function clock(time) {
  const mins = minutesOf(time)
  if (mins === null) return time || ''
  const h = Math.floor(mins / 60), m = mins % 60
  return `${h % 12 || 12}:${String(m).padStart(2, '0')} ${h < 12 ? 'am' : 'pm'}`
}

// "6:22 am" – "8:44 am" -> "6:22 – 8:44 am"; keeps both suffixes when they differ.
export function range(start, end) {
  const sa = start.slice(-2), sb = end.slice(-2)
  return sa === sb ? `${start.slice(0, -3)} – ${end}` : `${start} – ${end}`
}

// Filled for Good and Peak, a dashed outline for Fair, grey for Low.
export function pillStyle(band) {
  const c = RAMP[band]
  if (band === 'Peak' || band === 'Good') return { background: c, color: '#0d1620', border: `1.5px solid ${c}` }
  if (band === 'Fair') return { background: 'transparent', color: c, border: `1.5px dashed ${c}` }
  return { background: 'transparent', color: '#bdbdbd', border: '1.5px solid #5a5a5a' }
}

const cToF = c => c * 9 / 5 + 32

export function weatherText(w) {
  if (!w) return null
  const anomF = w.tmaxAnomC * 9 / 5
  const departure = Math.abs(anomF) < 3 ? 'near normal' : `${Math.round(Math.abs(anomF))}° ${anomF > 0 ? 'above' : 'below'} normal`
  const inches = w.precipMm / 25.4
  const rain = w.precipMm < 0.5 ? 'dry' : `${inches < 0.1 ? inches.toFixed(2) : inches.toFixed(1)} in rain`
  return `High ${Math.round(cToF(w.tmaxC))}°F, ${departure}; ${rain}; wind ${Math.round(w.windMs * 2.23694)} mph`
}

const capitalize = s => s ? s[0].toUpperCase() + s.slice(1) : s

// The API's why-list as labelled rows; weather in °F and mph, the collar caveat left for the footer.
export function whyRows(context) {
  const rows = []
  ;(context.why || []).forEach(line => {
    if (line.startsWith('Relative deer movement')) return
    if (line.startsWith('Rut: ')) rows.push({ key: 'RUT', text: capitalize(line.slice(5)) })
    else if (line.startsWith('Light: ')) rows.push({ key: 'LIGHT', text: lightText(context) })
    else if (line.startsWith('Weather: ')) context.weather && rows.push({ key: 'WEATHER', text: weatherText(context.weather) })
    else if (context.pressure && line === context.pressure.text) rows.push({ key: 'PRESSURE', text: line })
    else rows.push({ key: 'COVER', text: line })
  })
  return rows
}

const lightText = c =>
  `Sunrise ${clock(c.sunriseLocal)}, sunset ${clock(c.sunsetLocal)}, ${(+c.daylengthH).toFixed(1)} h of day`

export function caveat(context) {
  return 'Relative deer movement from GPS collars, not a count of deer' +
    (context.weather ? '' : '; no weather for this day') +
    '. Same cover, same day, same score: it rates the day here, not the stand.'
}

// The index of the curve row nearest a clock time, for placing windows on the chart.
function rowAt(curve, time) {
  const t = minutesOf(time)
  let best = 0, bestDiff = Infinity
  curve.forEach((r, i) => {
    const d = Math.abs(minutesOf(r.localTime) - t)
    if (d < bestDiff) { best = i; bestDiff = d }
  })
  return best
}

// Where "now" falls on the curve, as a fraction of its width; null when now is not in this day.
function nowAt(curve, now) {
  for (let i = 0; i < curve.length; i++) {
    const t0 = Date.parse(curve[i].utc)
    const t1 = i + 1 < curve.length ? Date.parse(curve[i + 1].utc) : t0 + (t0 - Date.parse(curve[i - 1].utc))
    if (now >= t0 && now < t1) return (i + (now - t0) / (t1 - t0)) / curve.length
  }
  return null
}

const pct = f => `${(f * 100).toFixed(2)}%`

export function buildDay(day, index, now = Date.now()) {
  const c = day.context, curve = day.curve || [], n = curve.length || 1
  const band = c.daylightBand || bandOf(c.daylightScore)
  const fallback = c.windowBand === 'Fair'
  const light0 = minutesOf(c.sunriseLocal) - LEGAL_LIGHT_MINUTES
  const light1 = minutesOf(c.sunsetLocal) + LEGAL_LIGHT_MINUTES
  const bi = BANDS.indexOf(band)
  const nowFrac = index === 0 ? nowAt(curve, now) : null

  const windows = (c.windows || []).map(w => {
    const part = capitalize(w.part)
    const color = fallback ? RAMP.Fair : RAMP[w.band] || RAMP.Good
    const style = fallback ? 'dashed' : 'solid'
    const a = rowAt(curve, w.start), b = rowAt(curve, w.end)
    return {
      tag: fallback ? `${part} · Fair run` : part,
      range: range(w.start, w.end),
      peak: w.peak,
      peakScore: w.peakScore,
      color,
      style,
      zone: { left: pct(a / n), width: pct(Math.max(1, b - a + 1) / n) }
    }
  })

  return {
    date: day.date,
    score: c.daylightScore,
    band,
    color: RAMP[band],
    meter: BANDS.map((b, j) => j <= bi ? RAMP[band] : OFF),
    seasonal: !c.weather,
    summary: c.summary,
    // Height is the half-hour's share of the day's peak activity, colour its score: drawn from the score, a late-October
    // midday would read as no deer. The score stands in for the height until the API sends relativeActivity.
    bars: curve.map(r => {
      const m = minutesOf(r.localTime)
      // Artemis's card says which half-hours are in legal light; the API's days leave it to the clock.
      const legal = typeof r.legalLight === 'boolean' ? r.legalLight : m >= light0 && m <= light1
      const share = r.relativeActivity ?? r.score / 100
      return { height: Math.max(2, Math.round(share * 80)), color: legal ? RAMP[bandOf(r.score)] : NIGHT, time: r.localTime, score: r.score }
    }),
    windows,
    sunrise: clock(c.sunriseLocal),
    sunset: clock(c.sunsetLocal),
    // The curve runs in solar time, so sunrise and sunset always sit at a quarter and three quarters.
    sunriseAt: '25%',
    sunsetAt: '75%',
    nowAt: nowFrac === null ? null : pct(nowFrac),
    // Artemis's card sends the rows and the caveat already worded (lib/artemis/deer-card).
    why: Array.isArray(c.whyRows) ? c.whyRows : whyRows(c),
    caveat: c.caveat || caveat(c),
    chartLabel: `Half-hour deer activity; ${windows.map(w => `${w.tag.toLowerCase()} ${w.range} peaking at ${w.peakScore}`).join(', ') || 'no strong window'}`
  }
}

const WEEKDAY = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat']
const MONTH = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec']

// "2026-11-08" -> { weekday: "Sun", label: "Sun, Nov 8" } without a timezone shift.
export function dateLabel(date) {
  const [y, m, d] = date.split('-').map(Number)
  const weekday = WEEKDAY[new Date(Date.UTC(y, m - 1, d)).getUTCDay()]
  return { weekday, label: `${weekday}, ${MONTH[m - 1]} ${d}` }
}

// The browser's local date, yyyy-MM-dd.
export function today(now = new Date()) {
  return `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}-${String(now.getDate()).padStart(2, '0')}`
}
